//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

// #region Imports
import { EthAddr, TxHash } from '@shared/SchemaTypes'
import { Contract, EventLog, Signer } from 'ethers'

import { ActivityEnum, ChainError, RoleEnum } from '@shared/Enums'

import { poolActions } from '@react/redux/poolSlice'

import { ThunkAction, UnknownAction } from '@reduxjs/toolkit'
import { PoolFactoryFlexAbi, PoolFlexAbi } from '@shared/Abis'

import { otAssert } from '@shared/conversions'

import { navActions } from '@react/redux/navSlice'

import { logOT } from '@shared/loggingShared'
import { ETH_ADDR_ZERO, ProviderWritable } from '@shared/SchemaTypes'
import { txUUID, wsServerActionPoolTX, wsServerActionPoolTXRequest } from '@shared/WsServerActions'
import { OtStoreState, assertRole, dispatchError } from '@react/redux/store'
import { UserConnection } from '@shared/DeploymentConfigTypes'
import { PerimeterChainAddressesInterface } from '@shared/jest.shared.helper'

export const createPoolFlexTx =
  (props: {
    poolConfigSettings: string[]
    name: string
    tokenSymbol: string
    liquidityAssetAddress: EthAddr
  }): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    const { poolConfigSettings, name, tokenSymbol, liquidityAssetAddress } = props
    const activity = ActivityEnum.POOL_CREATE
    try {
      logOT.info(activity, poolConfigSettings)
      const { webSocket } = getState().chainState
      const { chainConfigurationName, perimeterAddresses } = getState().chainState.chainConfiguration

      const { roleEnum, signer } = getState().chainState.userConnection as UserConnection
      if (roleEnum && perimeterAddresses && chainConfigurationName) {
        assertRole(roleEnum, RoleEnum.POOL_ADMIN)
        otAssert(!!signer, 'signer is not defined')

        const createPoolReturn = await createPoolFlex(
          perimeterAddresses,
          webSocket,
          signer as Signer,
          poolConfigSettings,
          name,
          tokenSymbol,
          liquidityAssetAddress,
          chainConfigurationName
        )
        logOT.info('createPoolReturn', createPoolReturn)
        const poolAddr: EthAddr = createPoolReturn.poolAddr
        dispatch(poolActions.creatPoolSuccess({ poolAddr }))

        dispatch(navActions.navigateTo({ url: `/pools/${poolAddr}` }))
      }
    } catch (e: any) {
      dispatch(poolActions.creatPoolFail())
      await dispatchError(dispatch, e, activity, 1047)
    }
  }

export const createPoolFlex = async (
  perimeterAddresses: PerimeterChainAddressesInterface,
  webSocket: WebSocket | undefined,
  poolAdminSigner: Signer,
  poolConfigSettings: string[],
  name: string,
  tokenSymbol: string,
  liquidityAssetAddress: EthAddr,
  chainConfigurationName: string
): Promise<{
  poolAddr: EthAddr
}> => {
  try {
    const providerWritable = poolAdminSigner.provider as ProviderWritable
    logOT.debug('createPool poolConfigSettings', poolConfigSettings, perimeterAddresses.PoolFactory, !!poolAdminSigner)
    const poolFactoryFlexContract = new Contract(
      perimeterAddresses.PoolFactoryFlex,
      PoolFactoryFlexAbi,
      poolAdminSigner
    )

    logOT.debug('PerimeterAddresses.PoolFactory,', perimeterAddresses.PoolFactoryFlex)

    logOT.info('addresses', poolConfigSettings)

    // otAssert(poolConfigSettings[1].startsWith('0x'))
    // otAssert(poolConfigSettings[2].startsWith('0x')) //feeCollectorAddress

    logOT.debug('liquidityAssetAddress,', poolConfigSettings, poolAdminSigner.getAddress())

    logOT.debug('Create Pool Flex', liquidityAssetAddress, poolConfigSettings, name, tokenSymbol)

    const uuid = txUUID()
    webSocket?.send(wsServerActionPoolTXRequest(ActivityEnum.POOL_CREATE, ETH_ADDR_ZERO, uuid, chainConfigurationName))

    const createdPoolTx = await poolFactoryFlexContract.createPool(
      liquidityAssetAddress,
      poolConfigSettings,
      name,
      tokenSymbol
    )

    logOT.debug('PerimeterAddresses.createdPoolTx,')

    const receipt = await createdPoolTx.wait(1)
    const poolCreatedEvent = receipt.logs?.find((event: EventLog) => event.eventName === 'PoolCreated')

    logOT.debug(' poolCreatedEvent ', poolCreatedEvent)
    if (!poolCreatedEvent) {
      logOT.error('poolCreatedEvent not found', receipt)
    }

    const poolAddr = poolCreatedEvent.args.addr as EthAddr

    webSocket?.send(
      wsServerActionPoolTX(
        ActivityEnum.POOL_CREATE,
        poolAddr,
        uuid,
        createdPoolTx.hash as TxHash,
        chainConfigurationName
      )
    )

    logOT.debug('createPool poolAddr', poolAddr)
    const Pool = new Contract(poolAddr, PoolFlexAbi, providerWritable)
    const poolControllerAddr = await Pool.poolController()
    otAssert(poolControllerAddr)

    logOT.debug('createPool Return', poolAddr)
    return { poolAddr }
  } catch (e: unknown) {
    const error = e as ChainError
    logOT.error('createPool error', error.reason ?? error)
    logOT.error(e)
    throw e
  }
}
